import * as React from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { SortDirection, Table } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { Features } from 'generated/featureDefinitions'
import { TimeInsightsEntitiesClockInStatusStatusEnum } from 'generated/resources/graphql'
import isEqual from 'lodash/isEqual'
import { default as orderByLodash } from 'lodash/orderBy'

import { observer } from 'mobx-react'

import useEmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import employeeStatuses from 'modules/core/components/EmployeeMultiFilters/hooks/utils/employeeStatuses'
import WithMultiSelectState from 'modules/core/components/WithMultiSelectState'
import createMemo from 'modules/core/hooks/createMemo'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import { orderBy } from 'modules/core/lib/filters'
import type FiltersForm from 'modules/core/lib/forms/Filters'

import { InconsistencyState } from 'modules/attendance/components/ClockIn/types'
import type CustomPeriodForm from 'modules/attendance/components/TimeTracking/Layout/form'
import {
  Inconsistencies,
  useShiftInconsistencies,
} from 'modules/attendance/hooks/useShiftInconsistencies'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { PopoverTypeToShow } from 'modules/attendance/lib/types'
import type { ClockInStatus } from 'modules/attendance/pages/CustomPeriodsPage'
import presences from 'modules/attendance/store/collections/presences'
import type Period from 'modules/attendance/store/models/Period'
import type { Status } from 'modules/attendance/store/models/Period'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'

import Body from './_Body'
import Header from './_Header'
import TableOptions from './_TableOptions'
import getFilterValue from './_filters/getFilterValue'
import { filterByEmployee } from './filtering'
import { sortingCustomPeriods } from './sorting'

const useFilter = createMemo(
  (
    filteredEmployees: Employee[],
    customPeriods: Period[],
    search: string,
    direction: SortDirection,
    attribute: string,
    inconsistencies: Map<string | number, Inconsistencies>
  ) => {
    const filtered = filterByEmployee(filteredEmployees, customPeriods, search)

    return orderByLodash(filtered, (p) => sortingCustomPeriods(p, attribute, inconsistencies), [
      direction,
    ])
  }
)

const useOrderEmployees = (form: FiltersForm, employees: Employee[]) => {
  const [orderedEmployees, setOrderedEmployees] = React.useState<{
    loading: boolean
    value: Employee[]
  }>({ loading: false, value: [] })

  React.useEffect(() => {
    const orderFn = async () => {
      setOrderedEmployees({
        loading: true,
        value: [],
      })

      const result = await orderBy(form, employees)

      setOrderedEmployees({
        loading: false,
        value: result,
      })
    }

    orderFn()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.get('sortDirection').value,
    form.get('sortAttribute').value,
    form.get('filterBy').value,
    employees.length,
  ])

  return orderedEmployees
}

interface Props {
  canConfirm: boolean
  customPeriods: Period[]
  currentTabState: Status
  filtersForm: FiltersForm
  hasOvertimeTimeOff: boolean
  customPeriodForm: CustomPeriodForm
  clockInStatuses: ClockInStatus[]
  canSendEmailReminder: boolean
  showHourDistributionColumn: boolean
}

interface DefaultSorting {
  direction: SortDirection
  attribute: string
}

const CustomPeriodsTable = ({
  canConfirm,
  filtersForm,
  customPeriods,
  currentTabState,
  customPeriodForm,
  hasOvertimeTimeOff,
  clockInStatuses,
  canSendEmailReminder,
  showHourDistributionColumn,
}: Props) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const [searchParams] = useSearchParams()
  const sortByAlerts = searchParams.get('sortBy') === 'alerts'
  const { featureAvailability: hasTimeTrackingInconsistencies } = useAttendanceFeatures(
    Features.time_tracking_time_alerts
  )
  const defaultSorting: DefaultSorting = {
    direction: 'asc',
    attribute: 'first_name',
  }
  if (hasTimeTrackingInconsistencies && sortByAlerts) {
    defaultSorting.direction = 'desc'
    defaultSorting.attribute = 'alert_counts'
  }
  const [compensatedEmployeeIds, setCompensatedEmployeeIds] = React.useState<number[]>([])
  const [compensatedAt, setCompensatedAt] = React.useState<string>('')
  const [direction, setDirection] = React.useState<SortDirection>(defaultSorting.direction)
  const [attribute, setAttribute] = React.useState<string>(defaultSorting.attribute)
  const [loadingPresences, setLoadingPresences] = React.useState(true)
  const employeesList = useOrderEmployees(filtersForm, employees.toArray())
  const [payrollCompensation, setPayrollCompensation] = React.useState<boolean>(false)

  const { periodType } = useParams()

  const { startOn, endOn } = customPeriodForm
  const from = dateToString(startOn, 'backend')
  const to = dateToString(endOn, 'backend')

  const { inconsistencies } = useShiftInconsistencies({
    dataType: PopoverTypeToShow.Count,
    startOn: from,
    endOn: to,
    state: InconsistencyState.pending,
  })

  useUnsafeEffect(() => {
    if (!canSendEmailReminder) {
      const clockInFilterIndex = filters?.findIndex(
        (f) => f?.fields?.field?.value === 'clockin_status'
      )
      if (!!clockInFilterIndex) {
        onRemoveFilter(clockInFilterIndex)
      }
    }

    presences.fetch().then(() => setLoadingPresences(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useUnsafeEffect(() => {
    if (!loadingPresences) {
      onApplyFilters(employeesList.value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presences.length, loadingPresences])

  const {
    filteredModels: filteredEmployees,
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
  } = useEmployeeMultiFilters<Employee>(getFilterValue, LOCAL_STORAGE_KEYS.timeTrackingFilters, {
    // NOTE: This is a workaround to load the clock in statuses only for time tracking page
    //       MultiFilters requires a major refactor to remove this
    clockInStatuses,
  })

  const filteredCustomPeriods = useFilter(
    filteredEmployees,
    customPeriods,
    debouncedSearch,
    direction,
    attribute,
    inconsistencies
  )

  let selectableCustomPeriods = filteredCustomPeriods.filter((cp) => cp.isApprovable)
  const selectable = !!selectableCustomPeriods.length || canSendEmailReminder

  if (canSendEmailReminder) {
    const nonWorkingEmployees = clockInStatuses.filter(
      (c) => c.status !== 'clocked_in' && c.status !== 'in_a_break'
    )
    const nonWorkingEmployeeIds = nonWorkingEmployees.map((c) => c?.employee?.id)
    selectableCustomPeriods = filteredCustomPeriods.filter((cp) =>
      nonWorkingEmployeeIds.find((id) => id !== cp.attributes.employee_id)
    )
  }

  const onSort = (_attribute: string, _direction: SortDirection) => {
    setDirection(_direction)
    setAttribute(_attribute)
  }

  const getClockInStatusValue = (
    clockInStatusFilter: string
  ): TimeInsightsEntitiesClockInStatusStatusEnum => {
    switch (clockInStatusFilter) {
      case 'clockIn':
        return 'clocked_in'
      case 'missingClockIn':
        return 'missing_clock_in'
      case 'onBreak':
        return 'in_a_break'
      default:
        return 'invalid_status'
    }
  }

  const getInconsistencyType = (value: string) => {
    switch (value) {
      case employeeStatuses.alertsAllTypes:
        return 'all'
      case employeeStatuses.missingClockIn:
        return 'missing_clock_in'
      case employeeStatuses.missingClockOut:
        return 'missing_clock_out'
      case employeeStatuses.insufficientBreak:
        return 'break_duration'
      case employeeStatuses.missingBreakTime:
        return 'missing_break'
    }
  }

  const applyEmployeesFilter = (): void => {
    if (filters.length > 0) {
      const filtersFullInformation = filters.map((filter) => {
        const field = filter.get('field').value
        const condition = filter.get('condition').value
        const value = filter.get('value').value

        return {
          field,
          condition,
          value: String(value),
          field_condition: `${field}_${condition}`,
        }
      })

      const joinOperator =
        filters.length <= 1 ? '' : ` ${logicOperator.values.logic_operator.toUpperCase()} `
      const combinedFilters = filtersFullInformation
        .map((f) => f.field_condition)
        .join(joinOperator)

      const trackingPayload = {
        time_period: customPeriodForm.values.period_type,
        where: filtersFullInformation.map((f) => f.field),
        number_of_filters: filters.length,
        join_operator: filters.length <= 1 ? null : logicOperator.values.logic_operator,
        full_filters: combinedFilters,
      }

      track(trackingEvents.timeTracking.applyTimeTrackingFilters, trackingPayload)
    }

    const clockInStatusFilter = filters.find((f) => f.fields.field?.value === 'clockin_status')
      ?.fields.value?.value

    if (!!clockInStatusFilter) {
      track(trackingEvents.timeTracking.applyRealTimeAttendanceFilter, {
        filter: getClockInStatusValue(clockInStatusFilter),
      })
    }

    const alertsFilterValue = filters.find((f) => f.fields.field?.value === 'alerts')?.fields.value
      ?.value

    if (alertsFilterValue) {
      track(trackingEvents.timeTracking.applyInconsistencyFilter, {
        inconsistency_type: getInconsistencyType(alertsFilterValue),
        time_range: periodType,
      })
    }
    onApplyFilters(employeesList.value)
  }

  const getOptions = (
    selected: Period[],
    onDeselectAll: () => void,
    onCompensate: (employeeIds: number[], timestamp: string) => void
  ) =>
    function Options() {
      return (
        <TableOptions
          search={search}
          filters={filters}
          onSearch={onSearch}
          selected={selected}
          onCancel={onCancel}
          canConfirm={canConfirm}
          filtersForm={filtersForm}
          onAddFilter={onAddFilter}
          onCompensate={onCompensate}
          onDeselectAll={onDeselectAll}
          logicOperator={logicOperator}
          onRemoveFilter={onRemoveFilter}
          currentTabState={currentTabState}
          customPeriodForm={customPeriodForm}
          payrollCompensation={payrollCompensation}
          canSendEmailReminder={canSendEmailReminder}
          onApplyFilters={() => applyEmployeesFilter()}
        />
      )
    }

  const areAllSelectablePeriodsSelected = (selected: Period[]): boolean => {
    // Simply comparing the length doesn't work because `selected` may contain
    // a period that is not visible anymore, so it's not in `selectableCustomPeriods`,
    // because of a filter or search applied later.
    if (!selectableCustomPeriods.length) return false

    return isEqual(
      new Set(selectableCustomPeriods.map((p) => p.id)),
      new Set(selected.map((s) => s.id))
    )
  }

  const onCompensate = (_employeeIds: number[], timestamp: string) => {
    setCompensatedEmployeeIds(_employeeIds)
    setCompensatedAt(timestamp)
  }

  return (
    <WithMultiSelectState items={selectableCustomPeriods}>
      {({ onDeselectAll, onSelectAll, onToggleRange, onToggleSelectedItem, selected }) => (
        <Table withTabs options={getOptions(selected, onDeselectAll, onCompensate)} fullWidth>
          <Header
            onSort={onSort}
            onSelectAll={onSelectAll}
            sortAttribute={attribute}
            sortDirection={direction}
            onDeselectAll={onDeselectAll}
            hasOvertimeTimeOff={hasOvertimeTimeOff}
            selected={areAllSelectablePeriodsSelected(selected)}
            showHourDistributionColumn={showHourDistributionColumn}
            selectable={selectable}
          />
          <Body
            selected={selected}
            selectable={selectable}
            onSelectAll={onSelectAll}
            onDeselectAll={onDeselectAll}
            onToggleRange={onToggleRange}
            compensatedAt={compensatedAt}
            currentTabState={currentTabState}
            clockInStatuses={clockInStatuses}
            inconsistencies={inconsistencies}
            customPeriodForm={customPeriodForm}
            loadingPresences={loadingPresences}
            customPeriods={filteredCustomPeriods}
            canSendEmailReminder={canSendEmailReminder}
            onToggleSelectedItem={onToggleSelectedItem}
            setPayrollCompensation={setPayrollCompensation}
            compensatedEmployeeIds={compensatedEmployeeIds}
            showHourDistributionColumn={showHourDistributionColumn}
          />
        </Table>
      )}
    </WithMultiSelectState>
  )
}

export default observer(CustomPeriodsTable)
